import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Slide } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255,65,150,0.4); }
  70% { box-shadow: 0 0 0 12px rgba(255,65,150,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,65,150,0); }
`;

const BellBox = styled(Box)({
  width: 48, height: 48, borderRadius: '50%',
  background: 'linear-gradient(135deg,#FF6B6B,#FF2BAA)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  animation: `${pulse} 2s infinite`, flexShrink: 0,
});

const AllowBtn = styled(Button)({
  background: 'linear-gradient(45deg,#FF6B6B,#FF2BAA)',
  borderRadius: '50px', color: '#fff', fontWeight: 800,
  textTransform: 'none', px: 3,
  '&:hover': { filter: 'brightness(1.1)' },
});

// Convert VAPID key for subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

const PushNotificationPrompt = ({ lang = 'en' }) => {
  const [show, setShow]         = useState(false);
  const [status, setStatus]     = useState('idle'); // idle | asking | subscribed | denied | unsupported

  const copy = {
    en: { title: "🔔 Get notified when your favorites go live!",
          sub: "We'll alert you when new girls come online. No spam.",
          allow: "Yes, notify me!", later: "Maybe later" },
    fr: { title: "🔔 Sois averti quand tes favorites sont en direct !",
          sub: "On t'alerte quand de nouvelles filles sont en ligne.", 
          allow: "Oui, me notifier !", later: "Plus tard" },
    es: { title: "🔔 ¡Recibe alertas cuando tus favoritas estén en vivo!",
          sub: "Te avisamos cuando nuevas chicas estén en línea.",
          allow: "¡Sí, notifícame!", later: "Quizás luego" },
    de: { title: "🔔 Benachrichtigung wenn deine Favoriten live sind!",
          sub: "Wir informieren dich wenn neue Girls online gehen.",
          allow: "Ja, benachrichtigen!", later: "Vielleicht später" },
  };
  const t = copy[lang] || copy.en;

  useEffect(() => {
    try {
      // Don't show if: push not supported
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        setStatus('unsupported'); return;
      }
      // Don't show if already decided
      let dismissed = null, subscribed = null;
      try {
        dismissed  = localStorage.getItem('push_dismissed');
        subscribed = localStorage.getItem('push_subscribed');
      } catch(e) {} // private mode blocks localStorage

      if (subscribed) { setStatus('subscribed'); return; }
      if (dismissed && Date.now() - parseInt(dismissed) < 86400000 * 3) return;

      // Show after 45 seconds on site (user is engaged)
      const timer = setTimeout(() => setShow(true), 45000);
      return () => clearTimeout(timer);
    } catch(e) {
      setStatus('unsupported');
    }
  }, []);

  const handleAllow = async () => {
    setStatus('asking');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus('denied');
        localStorage.setItem('push_dismissed', Date.now());
        setTimeout(() => setShow(false), 2000);
        return;
      }

      // Register service worker if not already
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      let subscription;
      if (VAPID_PUBLIC_KEY) {
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }

      // Save subscription to backend
      await fetch(`${process.env.REACT_APP_API_URL}/api/push-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription, lang }),
      }).catch(() => {});

      localStorage.setItem('push_subscribed', '1');
      setStatus('subscribed');
      setTimeout(() => setShow(false), 3000);
    } catch (e) {
      console.error('Push subscription failed:', e);
      setStatus('denied');
      setTimeout(() => setShow(false), 2000);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('push_dismissed', Date.now());
    setShow(false);
  };

  if (!show || status === 'unsupported') return null;

  return (
    <Slide direction="up" in={show} mountOnEnter unmountOnExit>
      <Box sx={{
        position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        width: { xs: 'calc(100% - 32px)', sm: 420 },
        bgcolor: '#fff', borderRadius: 3, p: 2,
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        zIndex: 9999, display: 'flex', alignItems: 'center', gap: 2,
        border: '1px solid rgba(255,65,150,0.2)',
      }}>
        <BellBox>
          <NotificationsIcon sx={{ color: '#fff', fontSize: 24 }} />
        </BellBox>

        <Box sx={{ flex: 1 }}>
          {status === 'subscribed' ? (
            <Typography sx={{ fontWeight: 700, color: '#4CAF50' }}>
              ✅ You're subscribed!
            </Typography>
          ) : status === 'denied' ? (
            <Typography sx={{ fontWeight: 700, color: '#666' }}>
              Notifications blocked in browser settings.
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 0.3 }}>
                {t.title}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.75rem', mb: 1 }}>
                {t.sub}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <AllowBtn size="small" onClick={handleAllow} disabled={status === 'asking'}>
                  {status === 'asking' ? '...' : t.allow}
                </AllowBtn>
                <Button size="small" onClick={handleDismiss}
                  sx={{ color: '#aaa', textTransform: 'none', fontSize: '0.78rem' }}>
                  {t.later}
                </Button>
              </Box>
            </>
          )}
        </Box>

        <CloseIcon onClick={handleDismiss}
          sx={{ color: '#ccc', cursor: 'pointer', fontSize: 18, alignSelf: 'flex-start',
            '&:hover': { color: '#999' } }} />
      </Box>
    </Slide>
  );
};

export default PushNotificationPrompt;
